import { Filter } from '@/pages/reports/ats-reports/components/filter';
import { LastRefreshedTime } from '@/pages/reports/ats-reports/components/last-refreshed-time';
import { DateGroup } from '@/pages/reports/non-ats-reports/shared/date-group';
import { TimeframeView } from '@/pages/reports/non-ats-reports/shared/timeframe';
import { customReportActions } from '@/store/custom-report';
import { getCustomReportData } from '@/store/custom-report';
import { staticReportActions } from '@/store/static-report';
import { getStaticReportData } from '@/store/static-report';
import { Box, Flex } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKFlags } from '@/hooks/use-k-flags';
import { KText } from '@/ui-library/components';

export const FiltersWrapper = ({
  selectedFilters,
  setSelectedFilters,
  filtersToShow = [
    'job_name',
    'job_type',
    'job_status',
    'primary_recruiter',
    'primary_hm',
    'department',
    'appl_location',
    'users',
  ],
  isLocalFilterApplied = false,
  setIsLocalFilterApplied,
  isStaticReport = false,
  reportType = 'pipeline',
  onFilterChange = undefined,
  defaultFilters = undefined,
}) => {
  const dispatch = useDispatch();
  const { feReportsV3, feSurvey } = useKFlags();

  const computedFiltersToShow =
    feSurvey && !isStaticReport
      ? [...filtersToShow, 'survey_name']
      : filtersToShow;

  const isDefaultFiltersPresent = Boolean(defaultFilters);
  const isEditMode = Boolean(onFilterChange);

  // Conditionally use custom or static report actions and selectors
  const { applyFilter, applyTimeFrame, applyViewBy } = isStaticReport
    ? staticReportActions
    : customReportActions;

  const { timeFrame } = useSelector(
    isStaticReport ? getStaticReportData : getCustomReportData
  );

  // Build filter data object for Redux dispatch from the filter array.
  // Called from event handlers directly instead of a sync useEffect,
  // avoiding a redundant render cycle after every selectedFilters update.
  const dispatchFilters = (filters) => {
    const filterData = filters.reduce(
      (acc, { filter_by, filter_condition, filter_by_values }) => {
        if (filter_by_values?.length) {
          acc[filter_by.value] = {
            [filter_condition.value]: filter_by_values.map(({ id }) => id),
          };
        }
        return acc;
      },
      {}
    );
    dispatch(applyFilter(filterData));
  };

  useEffect(() => {
    if (isLocalFilterApplied) return;

    if (isDefaultFiltersPresent) {
      if (defaultFilters) {
        if (
          defaultFilters.normalFilters &&
          Array.isArray(defaultFilters.normalFilters)
        ) {
          setSelectedFilters(defaultFilters.normalFilters);
          dispatchFilters(defaultFilters.normalFilters);
        }

        if (defaultFilters.timeframe) {
          dispatch(applyTimeFrame({ ...defaultFilters.timeframe }));
        }

        if (defaultFilters.dateGroup) {
          const dateGroupId =
            typeof defaultFilters.dateGroup === 'object'
              ? defaultFilters.dateGroup.id
              : defaultFilters.dateGroup;

          if (dateGroupId) {
            dispatch(applyViewBy({ id: dateGroupId }));
          }
        }
      } else {
        setSelectedFilters([]);
        dispatchFilters([]);
      }
      setIsLocalFilterApplied?.(true);
    } else {
      setSelectedFilters([]);
      dispatchFilters([]);
      setIsLocalFilterApplied?.(true);
    }
  }, [isLocalFilterApplied, defaultFilters, isDefaultFiltersPresent]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = (filters) => {
    setSelectedFilters(filters);
    dispatchFilters(filters);
    onFilterChange?.('normalFilters', filters);
  };

  const onReset = () => {
    setSelectedFilters([]);
    dispatchFilters([]);
    onFilterChange?.('normalFilters', []);
  };

  const onRemoveFilter = (filter) => {
    const index = selectedFilters.findIndex(
      (value) => value?.filter_by?.value === filter?.filter_by?.value
    );

    let newFilters = [...selectedFilters];
    if (index > -1) {
      newFilters = [
        ...newFilters.slice(0, index),
        ...newFilters.slice(index + 1),
      ];
    }

    setSelectedFilters(newFilters);
    dispatchFilters(newFilters);
    onFilterChange?.('normalFilters', newFilters);
  };

  const onChangeTimeFrame = (selected) => {
    dispatch(applyTimeFrame({ ...selected }));

    if (onFilterChange) {
      const timeframeData = selected.to
        ? { from: selected.from, to: selected.to }
        : { from: selected.from };

      onFilterChange('timeframe', timeframeData);
    }
  };

  const onChangeDateGroup = (selected) => {
    dispatch(applyViewBy({ id: selected.id }));
    onFilterChange?.('dateGroup', selected);
  };

  if (feReportsV3) {
    return (
      <Flex justifyContent="space-between">
        <Filter
          onReset={onReset}
          onSave={onSave}
          onRemoveFilter={onRemoveFilter}
          selected={selectedFilters}
          filtersToShow={computedFiltersToShow}
          triggerContent={isEditMode ? 'Set default filters' : 'Filters'}
          isEditMode={isEditMode}
          reportType={reportType}
        />

        <Flex height="fit-content" gap="12px" alignItems="center">
          {isEditMode && <KText variant="heading_4">Set default:</KText>}
          {reportType !== 'live_job_pipeline' && (
            <>
              <TimeframeView
                isEditMode={isEditMode}
                selected={timeFrame}
                onChange={onChangeTimeFrame}
                onChangeCustomTime={onChangeTimeFrame}
              />
              <DateGroup
                timeframe={timeFrame}
                selected={timeFrame?.date_group}
                onChange={onChangeDateGroup}
              />
            </>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Box>
      <Flex height="fit-content">
        <TimeframeView
          selected={timeFrame}
          onChange={onChangeTimeFrame}
          onChangeCustomTime={onChangeTimeFrame}
        />
        <Box ml="20px">
          <DateGroup
            timeframe={timeFrame}
            selected={timeFrame?.date_group}
            onChange={onChangeDateGroup}
          />
        </Box>
        <LastRefreshedTime scope="applications" />
      </Flex>
      <Box my="18px">
        <Filter
          onReset={onReset}
          onSave={onSave}
          onRemoveFilter={onRemoveFilter}
          selected={selectedFilters}
          filtersToShow={computedFiltersToShow}
          reportType={reportType}
        />
      </Box>
    </Box>
  );
};
